// src/encounter/lib/to-allergy-request.ts
// Build the wire bodies for the IPS-rail Allergies section. REUSES the curl-verified
// CreateAllergyBody / UpdateAllergyBody contracts (patient-chart-api.ts, locked 2026-05-19)
// and the patients-module hooks — this file only assembles the body from the rail's
// dialog selection + the existing record. Pure (no hooks, no network) so it is unit-testable
// and mirrors the Flags `buildCreateFlagBody` pattern.
//
// Three actions:
//  - add    → POST /api/Patients/CreateAllergy (CreateAllergyBody; category top-level).
//  - edit   → PUT  /api/Patients/allergy/{id}  (UpdateAllergyBody; category top-level).
//  - delete → PUT  /api/Patients/allergy/{id}  (UpdateAllergyBody, isDeleted:true; preserve all).
//
// Wire-shape rules are carried verbatim from the type docblocks (recorder preserved on edit,
// top-level category, 5-field Coding normalisation, literal "n/a" sentinels).
import type { CodeWordCase } from '@/types/patient-api';
import type {
  AllergyCodeOption,
  CreateAllergyBody,
  PatientAllergy,
  PerformerDetailsFull,
  UpdateAllergyBody,
} from '@/types/patient-chart-api';

/**
 * The allergy category a DRUG allergy carries (PROD-1998). Flutter stamps exactly this Coding when
 * its Drug toggle is on (`converter_utils.dart:309-314`) and reads it back as "drug" when
 * `category.code === "DA"` (`patient_allergies_model.dart:76-78`). NAB042 is the NABIDH allergy
 * category value set.
 */
export const DRUG_ALLERGY_CATEGORY: CodeWordCase = {
  code: 'DA',
  display: 'Drug allergy',
  system: 'NAB042',
  description: null,
  shortName: null,
};

/** Which picker the allergy form is searching: the org's medication catalogue, or AllergyCode. */
export type AllergenKind = 'drug' | 'non-drug';

/** True when a stored/picked category is the drug-allergy category (`code === "DA"`). */
export function isDrugAllergy(category: Partial<CodeWordCase> | string | null | undefined): boolean {
  return typeof category === 'object' && category !== null && category.code === 'DA';
}

/** The form's Drug / Non-drug toggle value for an existing record's category. */
export function allergenKindOf(category: Partial<CodeWordCase> | string | null | undefined): AllergenKind {
  return isDrugAllergy(category) ? 'drug' : 'non-drug';
}

/**
 * A medication picked from `SearchValueSets2?SearchFor=Medications` → the allergen the form
 * carries. The Coding is saved verbatim (`{code, display: name, system}`, as Flutter's
 * ValueSetPicker saves `valueCoding`), with the drug-allergy category attached so the request
 * builders put it at top level.
 */
export function medicationToAllergen(med: {
  code: string;
  name: string;
  system?: string | null;
}): AllergyCodeOption {
  return {
    code: med.code,
    display: med.name,
    system: med.system || null,
    description: null,
    shortName: null,
    category: DRUG_ALLERGY_CATEGORY,
  };
}

/**
 * Select-option key for a master Coding (reaction / severity) — PROD-2735.
 *
 * The master lists carry DUPLICATE codes (e.g. Anaphylaxis shares its code with Unknown, and Mild
 * shares `M` with Major), so keying on `code` alone made the Select save whichever row came first.
 * The key is the whole `system|code|display` triple, which stays unique while the codes collide.
 */
export function codingOptionKey(c: Partial<CodeWordCase>): string {
  return `${c.system ?? ''}|${c.code ?? ''}|${c.display ?? ''}`;
}

/**
 * The option key to preselect for a stored Coding: an exact code+display match first, then the
 * first code match, then a display match (some master rows ship `code: null`). `''` when nothing
 * matches.
 */
export function findCodingOptionKey(
  options: readonly CodeWordCase[] | null | undefined,
  stored: Partial<CodeWordCase> | null | undefined,
): string {
  if (!options || !stored) return '';
  const code = stored.code ?? null;
  const display = (stored.display ?? '').trim();
  const hit =
    (code != null ? options.find((o) => o.code === code && (o.display ?? '').trim() === display) : undefined) ??
    (code != null ? options.find((o) => o.code === code) : undefined) ??
    (display ? options.find((o) => (o.display ?? '').trim() === display) : undefined);
  return hit ? codingOptionKey(hit) : '';
}

/**
 * Map a key that may have been built from a STORED Coding (whose system can differ from the master
 * row's, e.g. null) onto the matching option's key: the exact key when it is an option, else the
 * code+display → code → display match of its parts. `''` when nothing matches.
 */
export function resolveCodingOptionKey(
  options: readonly CodeWordCase[] | null | undefined,
  key: string,
): string {
  if (!options || !key) return '';
  if (options.some((o) => codingOptionKey(o) === key)) return key;
  const first = key.indexOf('|');
  const second = key.indexOf('|', first + 1);
  if (first < 0 || second < 0) return '';
  const code = key.slice(first + 1, second);
  const display = key.slice(second + 1);
  return findCodingOptionKey(options, { code: code || null, display: display || null });
}

/** Resolve a Select key back to its master Coding. */
export function findCodingByOptionKey(
  options: readonly CodeWordCase[] | null | undefined,
  key: string,
): CodeWordCase | null {
  if (!options || !key) return null;
  return options.find((o) => codingOptionKey(o) === key) ?? null;
}

export interface AllergyRequestContext {
  /**
   * The encounter this item is being recorded IN — stamped on CREATE as the wire `encounter`.
   *
   * FLUTTER PARITY. Its shared clinical-info popup applies `data.encounter = encounterId` to EVERY
   * summary type before dispatching per type (`popup_widget.dart:152`), and the per-tab add paths do
   * the same (`allergies_tab.dart:785`, `history_tab.dart:1318,1490` → `encounter:
   * widget.encounterId`). Nearly every patient model carries the field — allergies, immunization,
   * conditions, procedures, medications, labs, flags, medical + social history.
   *
   * PA sent a hardcoded `encounter: null` here, so an item added on the pre-encounter had no record
   * of which visit produced it. Andrew, 2026-08-28: "when i call any api in the pre-encounter for
   * allergy, vaccine, family history, social history,.... we should add encounterId value."
   *
   * Optional on purpose. These builders are ENCOUNTER-ONLY — the patients-module chart has its own
   * path (`patients/services/use-create-allergy.ts` etc.), so an earlier version of this comment
   * claiming they "also serve the chart" was wrong. The reason it stays optional is that the
   * encounter itself can lack an id: a deep-link with no `createContext`, a Booked appointment
   * (where the open-phase create is deliberately gated), or an open-phase create that failed. In
   * those the rail still renders and still writes. Absent → `null`, exactly the previous behaviour.
   */
  encounterId?: string | null;
  patientId: string;
  /** Current user's userId GUID — stamped as `recorder` on CREATE only. */
  userId: string;
}

/** The dialog's resolved selection. `allergen` carries the picker row (with optional nested
 *  category — the AllergyCode row's own, or DRUG_ALLERGY_CATEGORY for a medication);
 *  `criticality` is the chosen severity Coding; onset is the resolved ISO (no-Z)
 *  partnered with the user's free-text display. */
export interface AllergyFormValue {
  allergen: AllergyCodeOption;
  criticality: CodeWordCase | null;
  /** Reaction Coding — optional in the rail (the summary doesn't surface it). */
  reaction?: CodeWordCase | null;
  /** ISO 8601 WITHOUT trailing Z (.NET model-binder convention). Null when unknown. */
  onset: string | null;
  /** User's free-text onset (e.g. "2024"). */
  onSetDisplay: string | null;
  note?: string | null;
}

export type AllergyAction =
  | { type: 'add'; value: AllergyFormValue }
  | { type: 'edit'; existing: PatientAllergy; value: AllergyFormValue }
  | { type: 'delete'; existing: PatientAllergy };

/** Normalise a Coding to the strict 5-field present-with-null shape (JSON.stringify drops
 *  `undefined`; Flutter wire parity is 5-field). Mirrors add-allergy-dialog's `normalizeCoding`. */
export function normalizeCoding(
  c: Partial<CodeWordCase> | null | undefined,
): CodeWordCase | null {
  if (!c) return null;
  return {
    code: c.code ?? null,
    display: c.display ?? null,
    system: c.system ?? null,
    description: c.description ?? null,
    shortName: c.shortName ?? null,
  };
}

function normalizePerformerDetails(
  pd: Partial<PerformerDetailsFull> | null | undefined,
): PerformerDetailsFull | null {
  if (!pd) return null;
  return {
    id: pd.id ?? null,
    name: pd.name ?? null,
    photo: pd.photo ?? null,
    speciality: pd.speciality
      ? {
          specialitySkillId: pd.speciality.specialitySkillId ?? null,
          code: pd.speciality.code
            ? {
                code: pd.speciality.code.code ?? null,
                displayName: pd.speciality.code.displayName ?? null,
                system: pd.speciality.code.system ?? null,
                description: pd.speciality.code.description ?? null,
                shortName: pd.speciality.code.shortName ?? null,
              }
            : null,
        }
      : null,
    timeZone: pd.timeZone ?? null,
    organizationId: pd.organizationId ?? null,
    roleName: pd.roleName ?? null,
  };
}

/**
 * CREATE body — category at TOP LEVEL, `code` a plain Coding.
 *
 * PROD-1998: this used to send `category: null` with the category nested under `code`. The
 * backend's CreatePatientAllergyRequest has only a top-level `category` (its `Code` class has no
 * category field), so the nested one was dropped and the allergy stored with no category — which
 * the NABIDH/Malaffi sender then failed on, silently leaving the allergy out of the AL1 segment.
 */
export function buildCreateAllergyBody(
  value: AllergyFormValue,
  ctx: AllergyRequestContext,
): CreateAllergyBody {
  return {
    identifier: null,
    clinicalStatus: 'Active',
    type: 'allergy',
    // Drug → DRUG_ALLERGY_CATEGORY (stamped by medicationToAllergen); non-drug → the picked
    // AllergyCode row's own category; null when that row has none (never invented).
    category: normalizeCoding(value.allergen.category),
    criticality: normalizeCoding(value.criticality),
    code: {
      code: value.allergen.code ?? null,
      display: value.allergen.display ?? null,
      system: value.allergen.system ?? null,
      description: value.allergen.description ?? null,
      shortName: value.allergen.shortName ?? null,
    },
    subject: ctx.patientId,
    dataAbsentReason: 'n/a',
    encounter: ctx.encounterId ?? null,
    onset: value.onset,
    onSetDisplay: value.onSetDisplay,
    recordedDate: null,
    recorder: ctx.userId,
    asserter: null,
    note: value.note?.trim() || null,
    reaction: {
      substance: normalizeCoding(value.reaction),
      onset: 'n/a',
      severity: 'n/a',
    },
    isDeleted: false,
    performerDetails: null,
    createdDate: null,
    speciality: null,
  };
}

/** Shared EDIT/DELETE body — round-trips the original record. `isDeleted` is the only
 *  difference between edit and soft-delete; `value` is omitted for delete (preserve all). */
function buildUpdateAllergyBody(
  existing: PatientAllergy,
  ctx: AllergyRequestContext,
  isDeleted: boolean,
  value?: AllergyFormValue,
): UpdateAllergyBody {
  // As on CREATE: the allergen's category goes TOP-LEVEL and `code` is a plain Coding.
  const allergen = value?.allergen ?? null;
  const existingCategory = typeof existing.category === 'object' ? existing.category : null;
  // Fall back to the record's own category only while the allergen is unchanged — a newly picked
  // row without a category must not inherit the old one (e.g. a drug's "DA" on a non-drug pick).
  const sameAllergen = !!allergen && (allergen.code ?? null) === (existing.code?.code ?? null);
  const topCategory = value
    ? normalizeCoding(allergen?.category ?? (sameAllergen ? existingCategory : null))
    : normalizeCoding(existingCategory);
  return {
    identifier: existing.identifier!,
    clinicalStatus: 'Active',
    type: 'allergy',
    category: topCategory,
    criticality: value ? normalizeCoding(value.criticality) : normalizeCoding(existing.criticality),
    code: value
      ? {
          code: allergen?.code ?? null,
          display: allergen?.display ?? null,
          system: allergen?.system ?? null,
          description: allergen?.description ?? null,
          shortName: allergen?.shortName ?? null,
        }
      : normalizeCoding(existing.code),
    subject: ctx.patientId,
    dataAbsentReason: 'n/a',
    /**
     * The CURRENT encounter wins, falling back to the record's own.
     *
     * FLUTTER PARITY (Andrew, 2026-08-28: "we will do as flutter"). Its shared popup assigns
     * `data.encounter = encounterId` BEFORE branching on `identifier != null`
     * (`popup_widget.dart:152`), so an edit re-stamps the item with the encounter it is being
     * edited IN — not the one that first recorded it. An earlier pass here preserved
     * `existing.encounter` on the reasoning that editing an old allergy shouldn't re-attribute it;
     * that was my call, and Andrew overruled it. It also fixes items PA wrote before the create-path
     * fix: they carry `encounter: null` and the next edit now heals them, where round-tripping would
     * have kept them null forever.
     *
     * THE `?? existing.encounter` IS NOT DECORATION. Flutter can assign unconditionally because its
     * popup guarantees an id first (`if (isBlank(encounterId)) createEncounter()`, initState). We
     * have no such guarantee: a deep-link with no `createContext`, a Booked appointment (open-phase
     * create gated), or a failed open-phase create all leave `encounterId` null while the rail still
     * renders and still writes. Assigning unconditionally in those cases would write null over a
     * real link and DESTROY the record's provenance. So: re-stamp when we have an encounter,
     * preserve when we don't.
     *
     * (An earlier version of this note justified the fallback with the patients-module chart. That
     * was wrong — the chart has its own request path and never calls these builders.)
     */
    encounter: ctx.encounterId ?? existing.encounter ?? null,
    onset: value ? value.onset : existing.onset ?? null,
    onSetDisplay: value ? value.onSetDisplay : existing.onSetDisplay ?? null,
    recordedDate: existing.recordedDate ?? null,
    // Preserve the ORIGINAL recording user — NOT the current editor (curl-locked).
    recorder: existing.recorder!,
    // The stored asserter, or NONE. Encounter owner ruling, 1 Nov 2026 ("chart rail + form
    // fields"): the recorder never stands in for an unknown asserter. This used to fall back to
    // `existing.recorder`, so an edit stamped the recording user as the person who asserted the
    // allergy. The wire accepts an absent asserter (`CreatePatientAllergyRequest.asserter` is a
    // `Guid?`, written as-is by `AllergyService` update), and create already sends null.
    asserter: existing.asserter ?? null,
    note: value ? value.note?.trim() || '' : existing.note ?? '',
    reaction: {
      substance: value ? normalizeCoding(value.reaction) : normalizeCoding(existing.reaction?.substance),
      onset: 'n/a',
      severity: 'n/a',
    },
    isDeleted,
    performerDetails: normalizePerformerDetails(existing.performerDetails),
    createdDate: existing.createdDate ?? null,
    // Flutter parity: top-level speciality ships null on Allergies UPDATE.
    speciality: null,
  };
}

export interface AllergyRequest {
  kind: 'create' | 'update';
  /** Allergy GUID for update path (`PUT /allergy/{id}`); undefined on create. */
  allergyId?: string;
  body: CreateAllergyBody | UpdateAllergyBody;
}

/**
 * Resolve an allergy action into the endpoint + body. Returns `null` when the action
 * is structurally invalid (e.g. edit/delete with no identifier, or edit/delete with no
 * original `recorder` — clobbering recorder corrupts the audit trail; block instead).
 */
export function buildAllergyRequest(
  action: AllergyAction,
  ctx: AllergyRequestContext,
): AllergyRequest | null {
  if (action.type === 'add') {
    return { kind: 'create', body: buildCreateAllergyBody(action.value, ctx) };
  }
  // edit / delete share the PUT path and require identifier + original recorder.
  if (!action.existing.identifier || !action.existing.recorder) return null;
  const isDeleted = action.type === 'delete';
  const value = action.type === 'edit' ? action.value : undefined;
  return {
    kind: 'update',
    allergyId: action.existing.identifier,
    body: buildUpdateAllergyBody(action.existing, ctx, isDeleted, value),
  };
}
